import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { fetchDynamicOptions } from '@de/utils/data-fetch/fetchDynamicOptions';
import { generateCacheKey } from '@de/utils/data-fetch/prefetchDynamicOptions';
import { Question, Step, Option } from '@de/types/types';
import { useEffect, useMemo, useRef, useState } from 'react';

interface DynamicOptionsConfig {
  id: string;
  dynamicOptions: NonNullable<Question['dynamicOptions']>;
  question: Question;
}

interface DynamicOptionsState {
  optionsMap: Record<string, Option[]>;
  loadingMap: Record<string, boolean>;
  /** Latest fetch error per question; cleared when that question is (re)fetched or succeeds. */
  errorsByQuestionId: Record<string, Error>;
}

const INITIAL_STATE: DynamicOptionsState = {
  optionsMap: {},
  loadingMap: {},
  errorsByQuestionId: {},
};

/**
 * Generates a cache key for a question based on its dependencies' values.
 * Used to detect when dependencies change and re-fetch is needed.
 */
const getQuestionDependencyKey = (
  question: Question,
  flowState: FlowState,
  steps: Step[],
): string => {
  return generateCacheKey(question, flowState, steps);
};

/**
 * Fetches dynamic options for all questions in a MultiQuestion component.
 * Returns a map of questionId -> fetched options and loading/error states.
 * Only shows loading state for questions whose dependencies have actually changed.
 *
 * Updates resolve per question (not batched in Promise.all) so fast requests
 * can render before slow ones. Stale responses are dropped when the dependency
 * key changed before the fetch completed.
 */
export const useMultiQuestionDynamicOptions = (
  questions: Question[],
  flowState: FlowState,
  steps: Step[],
  getCachedOptions?: (step: Question) => Option[] | undefined,
) => {
  const dynamicOptionsMap = useMemo(() => {
    const map: Record<string, DynamicOptionsConfig> = {};
    for (const question of questions) {
      if (question.dynamicOptions) {
        map[question.id] = {
          id: question.id,
          dynamicOptions: question.dynamicOptions,
          question,
        };
      }
    }
    return map;
  }, [questions]);

  const questionIds = useMemo(
    () => Object.keys(dynamicOptionsMap),
    [dynamicOptionsMap],
  );

  const [allResults, setAllResults] =
    useState<DynamicOptionsState>(INITIAL_STATE);

  const previousDependencyKeysRef = useRef<Record<string, string>>({});
  /** Avoid duplicate concurrent fetches for the same question + dependency key (e.g. Strict Mode). */
  const inFlightKeyByQuestionRef = useRef<Record<string, string | undefined>>(
    {},
  );

  const currentDependencyKeys = useMemo(() => {
    const keys: Record<string, string> = {};
    questionIds.forEach((questionId) => {
      const config = dynamicOptionsMap[questionId];
      keys[questionId] = getQuestionDependencyKey(
        config.question,
        flowState,
        steps,
      );
    });
    return keys;
  }, [questionIds, dynamicOptionsMap, flowState, steps]);

  const latestInputsRef = useRef({
    flowState,
    steps,
  });
  latestInputsRef.current = { flowState, steps };

  /** Ref avoids effect churn when the parent passes an inline `getCachedOptions` each render. */
  const getCachedOptionsRef = useRef(getCachedOptions);
  getCachedOptionsRef.current = getCachedOptions;

  useEffect(() => {
    let cancelled = false;

    if (questionIds.length === 0) {
      setAllResults(INITIAL_STATE);
      return;
    }

    const questionsToFetch = questionIds.filter((questionId) => {
      const currentKey = currentDependencyKeys[questionId];
      const previousKey = previousDependencyKeysRef.current[questionId];
      return !previousKey || previousKey !== currentKey;
    });

    if (questionsToFetch.length === 0) return;

    type CachedHit = {
      questionId: string;
      options: Option[];
      keyAtStart: string;
    };
    const cachedHits: CachedHit[] = [];
    const needsNetwork: string[] = [];

    for (const questionId of questionsToFetch) {
      const stepConfig = dynamicOptionsMap[questionId];
      const keyAtStart = currentDependencyKeys[questionId];
      let fromCache: Option[] | undefined;
      const resolveCache = getCachedOptionsRef.current;
      if (resolveCache) {
        const cached = resolveCache(stepConfig.question);
        // Any defined array (including empty) is a valid prefetch/cache hit.
        if (cached !== undefined) {
          fromCache = cached;
        }
      }
      if (fromCache !== undefined) {
        cachedHits.push({
          questionId,
          options: fromCache,
          keyAtStart,
        });
      } else {
        needsNetwork.push(questionId);
      }
    }

    if (cachedHits.length > 0) {
      for (const hit of cachedHits) {
        previousDependencyKeysRef.current = {
          ...previousDependencyKeysRef.current,
          [hit.questionId]: hit.keyAtStart,
        };
      }
    }

    setAllResults((prev) => {
      const newErrorsByQuestionId = { ...prev.errorsByQuestionId };
      for (const qid of questionsToFetch) {
        delete newErrorsByQuestionId[qid];
      }
      const newOptionsMap = { ...prev.optionsMap };
      const newLoadingMap = { ...prev.loadingMap };
      for (const hit of cachedHits) {
        newOptionsMap[hit.questionId] = hit.options;
        newLoadingMap[hit.questionId] = false;
      }
      for (const qId of needsNetwork) {
        newLoadingMap[qId] = true;
      }
      return {
        optionsMap: newOptionsMap,
        loadingMap: newLoadingMap,
        errorsByQuestionId: newErrorsByQuestionId,
      };
    });

    needsNetwork.forEach((questionId) => {
      const stepConfig = dynamicOptionsMap[questionId];
      const keyAtStart = currentDependencyKeys[questionId];

      if (inFlightKeyByQuestionRef.current[questionId] === keyAtStart) {
        return;
      }
      inFlightKeyByQuestionRef.current[questionId] = keyAtStart;

      void (async () => {
        try {
          let options: Option[] | null = null;
          let error: Error | null = null;

          try {
            const { flowState: fs, steps: st } = latestInputsRef.current;
            options = await fetchDynamicOptions(
              stepConfig.dynamicOptions,
              fs,
              st,
            );
          } catch (err) {
            error = err instanceof Error ? err : new Error(String(err));
          }

          if (cancelled) {
            return;
          }

          const { flowState: fsAfter, steps: stAfter } =
            latestInputsRef.current;
          const latestKey = getQuestionDependencyKey(
            stepConfig.question,
            fsAfter,
            stAfter,
          );
          if (latestKey !== keyAtStart) {
            // Stale response: do not clear loading if a newer fetch for this question
            // already registered (inFlight holds the newer key); that request owns loading.
            if (
              !cancelled &&
              inFlightKeyByQuestionRef.current[questionId] === keyAtStart
            ) {
              setAllResults((prev) => ({
                ...prev,
                loadingMap: { ...prev.loadingMap, [questionId]: false },
              }));
            }
            return;
          }

          // Record successful dependency key only after success so failed fetches can retry.
          if (!error && options != null) {
            previousDependencyKeysRef.current = {
              ...previousDependencyKeysRef.current,
              [questionId]: keyAtStart,
            };
          }

          if (!cancelled) {
            setAllResults((prev) => {
              const newOptionsMap = { ...prev.optionsMap };
              const newLoadingMap = { ...prev.loadingMap };
              const newErrorsByQuestionId = { ...prev.errorsByQuestionId };

              newLoadingMap[questionId] = false;
              if (error) {
                newErrorsByQuestionId[questionId] = error;
              } else if (options != null) {
                newOptionsMap[questionId] = options;
                delete newErrorsByQuestionId[questionId];
              }

              return {
                optionsMap: newOptionsMap,
                loadingMap: newLoadingMap,
                errorsByQuestionId: newErrorsByQuestionId,
              };
            });
          }
        } finally {
          if (inFlightKeyByQuestionRef.current[questionId] === keyAtStart) {
            delete inFlightKeyByQuestionRef.current[questionId];
          }
        }
      })();
    });

    return () => {
      cancelled = true;
    };
  }, [questionIds, currentDependencyKeys, dynamicOptionsMap, flowState, steps]);

  return {
    ...allResults,
    isAnyLoading: Object.values(allResults.loadingMap).some(
      (loading) => loading,
    ),
  };
};
